import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { useCurrentConversationContext } from "./current-conversation-provider";
import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { startConversation } from "./start-conversation.thunk";
import { loadConversation } from "./load-conversation.thunk";
import { sendMessage } from "./send-message.thunk";
import { cancelPrompt } from "./cancel-prompt.thunk";
import { createMessageRating } from "./create-message-rating.thunk";
import { useConversationHistoryActions } from "../conversation-history/use-conversation-history-actions.hook";
import { retry } from "./retry.thunk";
import { updateMessageRating } from "./update-message-rating.thunk";
import ConversationMessageFeedbackRecord from "models/view-models/conversations/conversation-message-feedback-record";
import { edit } from "./edit.thunk";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import {
    updateMemoryStatus,
    UpdateMemoryStatusParams,
} from "./update-memory-status.thunk";
import {
    updateToolCallStatus,
    UpdateToolCallStatusParams,
} from "./update-tool-call-status.thunk";

export interface UseConversationActionsHookResult {
    /**
     * Creates a new conversation.
     * This method will update the state to indicate that a new conversation is being created.
     * Once the conversation is created, it will update the state to reflect the new conversation.
     * If an error occurs during the creation, it will update the state with the error message.
     */
    startConversation: (message?: ConversationMessageRecord) => void;

    /**
     * Sends a message in the current conversation.
     * This method will update the state to indicate that a message is being sent.
     * Once the message is sent, it will update the state to reflect the new message in the conversation.
     * If an error occurs during sending, it will update the state with the error message.
     */
    sendPrompt: (
        prompt: string,
        applicationContext?: ConversationApplicationContextRecord
    ) => void;

    /**
     * Loads a conversation by its ID.
     * This method will update the state to indicate that a conversation is being loaded.
     */
    loadConversation(id: string): void;

    /**
     * Clears the current conversation.
     * This method will reset the current conversation state to start a new conversation.
     */
    clearConversation(): void;

    /**
     * Cancels the current prompt.
     * This method will abort the ongoing message sending process if it is in progress.
     */
    cancelPrompt: () => void;

    /*
     * Handles the thumbs up click event for a message.
     * This method will update the state to reflect the thumbs up action on a message.
     */
    createMessageRating: (feedback: ConversationMessageFeedbackRecord) => void;

    /**
     * Handles updating a message rating.
     * This method will update the state to reflect the updated rating on a message.
     */
    updateMessageRating: (feedback: ConversationMessageFeedbackRecord) => void;

    /**
     * Accepts or rejects a suggested memory on a message and updates the state to reflect
     * the new memory status.
     */
    updateMemoryStatus: (params: UpdateMemoryStatusParams) => Promise<void>;
    updateToolCallStatus: (params: UpdateToolCallStatusParams) => Promise<void>;

    /**
     * Handles regenerating a response for a given message. Finds the user message that prompted the
     * response and resends it. All messages between the user message and the response (inclusive)
     * are removed client side in order to reset the current conversation to a Thinnking state
     */
    retry: (
        message: ConversationMessageRecord,
        applicationContext?: ConversationApplicationContextRecord
    ) => void;

    edit: (
        content: string,
        messageId: string | undefined,
        applicationContext?: ConversationApplicationContextRecord
    ) => void;
}

/**
 * Custom hook to provide actions for managing the a Conversation.
 */
export function useConversationActions(): UseConversationActionsHookResult {
    const atom = useCurrentConversationContext();
    const dispatch = useSetAtom(atom);

    // TODO: possible refactor after the conversation v2 updates
    const { refreshConversationHistory } = useConversationHistoryActions();

    return {
        startConversation: useCallback(
            (message?: ConversationMessageRecord) => {
                dispatch(startConversation(message));
            },
            [dispatch]
        ),

        loadConversation: useCallback(
            (id: string) => {
                dispatch(loadConversation(id));
            },
            [dispatch]
        ),

        sendPrompt: useCallback(
            (
                prompt: string,
                applicationContext?: ConversationApplicationContextRecord
            ) => {
                dispatch(
                    sendMessage({
                        prompt,
                        refreshConversationHistory,
                        applicationContext,
                    })
                );
            },
            [dispatch, refreshConversationHistory]
        ),

        clearConversation: useCallback(() => {
            dispatch({
                type: "CLEAR_CONVERSATION",
            });
        }, [dispatch]),

        cancelPrompt: useCallback(() => {
            dispatch(cancelPrompt());
        }, [dispatch]),

        createMessageRating: useCallback(
            (feedback: ConversationMessageFeedbackRecord) => {
                dispatch(createMessageRating(feedback));
            },
            [dispatch]
        ),

        updateMessageRating: useCallback(
            (feedback: ConversationMessageFeedbackRecord) => {
                dispatch(updateMessageRating(feedback));
            },
            [dispatch]
        ),

        updateMemoryStatus: useCallback(
            (params: UpdateMemoryStatusParams) => {
                return dispatch(updateMemoryStatus(params));
            },
            [dispatch]
        ),

        updateToolCallStatus: useCallback(
            (params: UpdateToolCallStatusParams) => {
                return dispatch(updateToolCallStatus(params));
            },
            [dispatch]
        ),

        retry: useCallback(
            (
                message: ConversationMessageRecord,
                applicationContext?: ConversationApplicationContextRecord
            ) => {
                dispatch(retry({ message, applicationContext }));
            },
            [dispatch]
        ),

        edit: useCallback(
            (
                content: string,
                messageId: string | undefined,
                applicationContext?: ConversationApplicationContextRecord
            ) => {
                dispatch(edit({ content, messageId, applicationContext }));
            },
            [dispatch]
        ),
    };
}
